import { Flex, Text } from '@chakra-ui/react'
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'

import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'
import type { ToastLike } from '@mangovoice/mango-ui'
import type { ExtensionComparisonDirection } from '@mangovoice/types'

import { type ReportingApi, ReportingApiProvider } from '../api'
import ExtensionComparisonChart from '../components/ExtensionComparisonChart'
import ExtensionComparisonTable from '../components/ExtensionComparisonTable'
import { ReportingDashboardShell } from '../components/ReportingDashboardShell'
import type { ViewType } from '../components/ViewToggle'
import { useDateContext, useIsDateRangeTooLarge } from '../hooks/useDateContext'
import { type DashboardExportHandle, useExportCsv } from '../hooks/useExportCsv'
import {
  type ExtensionNameMap,
  formatExtensionLabel,
  useExtensionNames,
} from '../hooks/useExtensionNames'
import { useGetExtensionComparison } from '../hooks/useGetExtensionComparison'
import {
  ReportingI18nProvider,
  type ReportingTranslationLike,
  useReportingI18n,
} from '../i18n'
import { ShowMoreButton } from '../shared/ShowMoreButton'
import type { DateRange, ExtensionData } from '../types'

const CHART_BATCH = 25
const TABLE_BATCH = 50
const EMPTY_EXTENSIONS: string[] = []

interface ExtensionComparisonDashboardInnerProps {
  sharedDateRange: DateRange
  selectedPbxId: number
  timeZone?: string
  direction: ExtensionComparisonDirection
  selectedExtensions?: string[]
  onExportAvailabilityChange?: (canExport: boolean) => void
  onAvailableExtensionsChange?: (extensions: string[]) => void
  onExtensionNameMapChange?: (nameMap: ExtensionNameMap) => void
  toast?: ToastLike
}

export type ExtensionComparisonDashboardHandle = DashboardExportHandle

const ExtensionComparisonDashboardInner = forwardRef<
  ExtensionComparisonDashboardHandle,
  ExtensionComparisonDashboardInnerProps
>(
  (
    {
      sharedDateRange,
      selectedPbxId,
      timeZone,
      direction,
      selectedExtensions = EMPTY_EXTENSIONS,
      onExportAvailabilityChange,
      onAvailableExtensionsChange,
      onExtensionNameMapChange,
      toast,
    },
    ref,
  ) => {
    const { text } = useReportingI18n()
    const [viewType, setViewType] = useState<ViewType>('chart')
    const batchSize = viewType === 'chart' ? CHART_BATCH : TABLE_BATCH
    const [displayLimit, setDisplayLimit] = useState(batchSize)

    const dateContext = useDateContext(sharedDateRange, timeZone)
    const isDateRangeTooLarge = useIsDateRangeTooLarge(sharedDateRange)
    const { nameMap: extensionNameMap, isFetching: isNamesFetching } =
      useExtensionNames({
        pbxId: selectedPbxId,
        enabled: !!dateContext,
      })

    useEffect(() => {
      setDisplayLimit(viewType === 'chart' ? CHART_BATCH : TABLE_BATCH)
    }, [sharedDateRange, direction, selectedExtensions, viewType])

    const allExtensionsRequest = useMemo(() => {
      if (!dateContext) return undefined

      return {
        start: dateContext.startDateUtc,
        end: dateContext.endDateUtc,
        pbx: selectedPbxId.toString(),
        ...(direction !== 'all' && { direction }),
      }
    }, [dateContext, selectedPbxId, direction])

    const extensionComparisonRequest = useMemo(() => {
      if (!dateContext) return undefined

      return {
        start: dateContext.startDateUtc,
        end: dateContext.endDateUtc,
        pbx: selectedPbxId.toString(),
        ...(direction !== 'all' && { direction }),
        ...(selectedExtensions.length > 0 && {
          extension: selectedExtensions,
        }),
      }
    }, [dateContext, selectedPbxId, direction, selectedExtensions])

    const { data: allExtensionsData } = useGetExtensionComparison({
      request: allExtensionsRequest,
      enabled: !!dateContext && !isDateRangeTooLarge,
    })

    const {
      data: extensionComparisonData,
      isFetching: isLoading,
      error: apiError,
    } = useGetExtensionComparison({
      request: extensionComparisonRequest,
      enabled: !!dateContext && !isDateRangeTooLarge,
    })

    const hasError = !!apiError

    const onAvailableExtensionsChangeRef = useRef(onAvailableExtensionsChange)
    onAvailableExtensionsChangeRef.current = onAvailableExtensionsChange

    const onExtensionNameMapChangeRef = useRef(onExtensionNameMapChange)
    onExtensionNameMapChangeRef.current = onExtensionNameMapChange

    useEffect(() => {
      if (!allExtensionsData) return
      const availableExts = allExtensionsData.segments.map((s) => s.extension)
      onAvailableExtensionsChangeRef.current?.(availableExts)
    }, [allExtensionsData])

    useEffect(() => {
      onExtensionNameMapChangeRef.current?.(extensionNameMap)
    }, [extensionNameMap])

    const allExtensionData = useMemo<ExtensionData[]>(() => {
      const segments = extensionComparisonData?.segments || []
      const retiredLabel = isNamesFetching
        ? undefined
        : text('extensionComparison.retired', 'Retired')
      return segments
        .map((segment) => {
          const calls = segment.counts?.calls ?? 0
          const seconds = segment.counts?.seconds ?? 0
          const totalMinutes = Math.round(seconds / 60)
          const averageDuration = calls > 0 ? Math.round(seconds / calls) : 0
          return {
            extension: segment.extension,
            displayLabel: formatExtensionLabel(
              segment.extension,
              extensionNameMap,
              retiredLabel,
            ),
            totalCalls: calls,
            totalSeconds: seconds,
            totalMinutes,
            averageDuration,
          }
        })
        .sort((a, b) => a.displayLabel.localeCompare(b.displayLabel))
    }, [extensionComparisonData, extensionNameMap, isNamesFetching, text])

    const extensionData = useMemo(
      () => allExtensionData.slice(0, displayLimit),
      [allExtensionData, displayLimit],
    )

    const hasMoreData = displayLimit < allExtensionData.length
    const remainingCount = allExtensionData.length - displayLimit

    const totals = useMemo(() => {
      if (allExtensionData.length === 0) {
        return {
          totalCalls: 0,
          totalMinutes: 0,
          extensionCount: 0,
          averageCalls: 0,
          averageMinutes: 0,
          topExtension: text(
            'extensionComparisonDashboard.notAvailable',
            'N/A',
          ),
          topExtensionCalls: 0,
        }
      }

      const totalCalls = allExtensionData.reduce(
        (sum, item) => sum + item.totalCalls,
        0,
      )
      const totalSeconds = allExtensionData.reduce(
        (sum, item) => sum + item.totalSeconds,
        0,
      )
      const totalMinutes = Math.round(totalSeconds / 60)
      const extensionCount = allExtensionData.length
      const averageCalls =
        extensionCount > 0 ? Math.round(totalCalls / extensionCount) : 0
      const averageMinutes =
        extensionCount > 0 ? Math.round(totalMinutes / extensionCount) : 0
      const topByCalls = allExtensionData.reduce((best, item) =>
        item.totalCalls > best.totalCalls ? item : best,
      )
      const na = text('extensionComparisonDashboard.notAvailable', 'N/A')

      return {
        totalCalls,
        totalMinutes,
        extensionCount,
        averageCalls,
        averageMinutes,
        topExtension: topByCalls?.displayLabel ?? na,
        topExtensionCalls: topByCalls?.totalCalls ?? 0,
      }
    }, [allExtensionData, text])

    const csvRows = useMemo(() => {
      const h = {
        extension: text(
          'dashboards.csvExport.headers.extensionComparison.extension',
          'Extension',
        ),
        totalCalls: text(
          'dashboards.csvExport.headers.extensionComparison.totalCalls',
          'Total Calls',
        ),
        totalMinutes: text(
          'dashboards.csvExport.headers.extensionComparison.totalMinutes',
          'Total Minutes',
        ),
        totalDurationSeconds: text(
          'dashboards.csvExport.headers.extensionComparison.totalDurationSeconds',
          'Total Duration (seconds)',
        ),
        averageDurationSeconds: text(
          'dashboards.csvExport.headers.extensionComparison.averageDurationSeconds',
          'Average Duration (seconds)',
        ),
      }

      return allExtensionData.map((row) => ({
        [h.extension]: row.displayLabel,
        [h.totalCalls]: row.totalCalls,
        [h.totalMinutes]: row.totalMinutes,
        [h.totalDurationSeconds]: row.totalSeconds,
        [h.averageDurationSeconds]: row.averageDuration,
      }))
    }, [allExtensionData, text])

    useExportCsv({
      ref,
      csvRows,
      filenamePrefix: 'extension_comparison',
      isLoading,
      hasError,
      toast,
      onExportAvailabilityChange,
    })

    const counters = useMemo(
      () => [
        {
          value: totals.totalCalls,
          label: text(
            'extensionComparisonDashboard.counters.totalCalls.title',
            'Total Calls',
          ),
          subtitle: text(
            'extensionComparisonDashboard.counters.totalCalls.subtitle',
            'Total calls across all extensions',
          ),
        },
        {
          value: totals.averageCalls,
          label: text(
            'extensionComparisonDashboard.counters.averageCalls.title',
            'Average per Extension',
          ),
          subtitle: text(
            'extensionComparisonDashboard.counters.averageCalls.subtitle',
            'Average calls per extension',
          ),
        },
        {
          value: totals.averageMinutes,
          label: text(
            'extensionComparisonDashboard.counters.averageMinutes.title',
            'Average Minutes per Extension',
          ),
          subtitle: text(
            'extensionComparisonDashboard.counters.averageMinutes.subtitle',
            'Average minutes per extension',
          ),
        },
        {
          value: totals.topExtensionCalls,
          label: text(
            'extensionComparisonDashboard.counters.topExtension.title',
            'Top Performer (Calls)',
          ),
          subtitle: totals.topExtension,
        },
      ],
      [totals, text],
    )

    return (
      <ReportingDashboardShell
        timeZone={timeZone}
        dateContextReady={!!dateContext}
        isDateRangeTooLarge={isDateRangeTooLarge}
        isLoading={
          isLoading || (isNamesFetching && extensionNameMap.size === 0)
        }
        hasError={hasError}
        hasData={allExtensionData.length > 0}
        viewType={viewType}
        onViewTypeChange={setViewType}
        counters={counters}
        viewToggleMarginBottom="2.5rem"
        i18nKeys={{
          invalidDateRange: 'extensionComparisonDashboard.invalidDateRange',
          gettingPbx: 'dashboards.gettingPbx',
          dateRangeTooLarge: 'extensionComparisonDashboard.dateRangeTooLarge',
          dateRangeAdjustPrompt:
            'extensionComparisonDashboard.dateRangeAdjustPrompt',
          errorText: 'extensionComparisonDashboard.dashboard.errorText',
          noDataText: 'extensionComparisonDashboard.dashboard.noDataText',
        }}
        chartHeaderContent={
          <Flex alignItems="center" justifyContent="space-between" mb="1rem">
            <Text {...textStyles[textStyleKeys.Body2_Bold]}>
              {text(
                'extensionComparisonDashboard.chartTitle',
                'Call Volume by Extension',
              )}
            </Text>
            {viewType === 'chart' && (
              <Text
                {...textStyles[textStyleKeys.Body3]}
                color="fg.muted"
                mr="60px"
              >
                {text(
                  'extensionComparisonDashboard.showingCount',
                  'Showing {{displayed}} of {{total}}',
                  {
                    displayed: extensionData.length,
                    total: allExtensionData.length,
                  },
                )}
              </Text>
            )}
          </Flex>
        }
        chartContent={
          <ExtensionComparisonChart
            data={extensionData}
            hasMore={hasMoreData}
            remaining={remainingCount}
            onShowMore={() => setDisplayLimit((prev) => prev + batchSize)}
          />
        }
        tableContent={
          <>
            <ExtensionComparisonTable
              data={extensionData}
              grandTotalCalls={totals.totalCalls}
              grandTotalMinutes={totals.totalMinutes}
            />
            {hasMoreData && (
              <ShowMoreButton
                remaining={remainingCount}
                onClick={() => setDisplayLimit((prev) => prev + batchSize)}
              />
            )}
          </>
        }
      />
    )
  },
)

export interface ExtensionComparisonDashboardProps extends ExtensionComparisonDashboardInnerProps {
  api: ReportingApi
  t?: ReportingTranslationLike
  locale?: string
}

export const ExtensionComparisonDashboard = forwardRef<
  ExtensionComparisonDashboardHandle,
  ExtensionComparisonDashboardProps
>(({ api, t, locale, ...rest }, ref) => {
  return (
    <ReportingApiProvider api={api}>
      <ReportingI18nProvider t={t} locale={locale}>
        <ExtensionComparisonDashboardInner ref={ref} {...rest} />
      </ReportingI18nProvider>
    </ReportingApiProvider>
  )
})

export default ExtensionComparisonDashboard
